/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react';
import ListUtilityLoadingSpinner from '../CircularProgress/ListUtilityLoadingSpinner';

type RenderItemArgs = {
  index: number;
  item: any;
};

type LoadDataArgs = {
  skip: number;
  limit: number;
};

export interface LazyLoadingListProps<T> {
  forceUpdate?: boolean; // NOTE: this is dangerous, use with caution - if managed correctly, should properly handle cases where list order changes, but list ref does not
  limit?: number;
  data?: Array<T>;
  renderItem: (props: RenderItemArgs) => JSX.Element;
  loadData?: (props: LoadDataArgs) => Promise<Array<T>>;
  loadWhenDistanceToBottom?: number;
  LoadingComponent?: JSX.Element | undefined;
}

// not true distance - just a guess
const DISTANCE_TO_BOTTOM = 1000;
const LIMIT = 50;

const LazyLoadingList = <T extends object>({
  forceUpdate = false,
  limit = LIMIT,
  data = [],
  loadData,
  renderItem,
  loadWhenDistanceToBottom = DISTANCE_TO_BOTTOM,
  LoadingComponent
}: LazyLoadingListProps<T>): JSX.Element => {
  const [internalData, setInternalData] = useState([] as T[]);
  const [loading, setLoadingData] = useState(false);

  // always reset internal state if data ref changes
  useEffect(() => {
    setInternalData(data?.slice(0, limit));
  }, [data, limit]);

  useEffect(() => {
    if (forceUpdate) {
      setInternalData(data?.slice(0, limit));
    }
  }, [data, forceUpdate, limit]);

  useEffect(() => {
    async function onScroll(e: any): Promise<void> {
      if (internalData.length === data.length) return;

      // this is not the true distance to the bottom - just a guess - because currently we do not have
      // access to the window dimensions due to having height:100% on the body element
      // we should look into changing that in the future
      const distanceToBottom = e.target.scrollHeight - e.target.scrollTop;

      // const doc = document.documentElement;
      // const left = (window.pageXOffset || doc.scrollLeft) - (doc.clientLeft || 0);
      // const top = (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);

      if (distanceToBottom <= loadWhenDistanceToBottom) {
        const nextLength = internalData.length + limit;
        const toIndex = nextLength > data.length ? data.length : nextLength;

        setLoadingData(true);
        if (loadData) {
          const responseData =
            (await loadData({ skip: internalData.length, limit }).catch(() => undefined)) ?? [];
          setInternalData([...internalData, ...responseData]);
          setLoadingData(false);
        } else {
          setTimeout(() => {
            setLoadingData(false);
            setInternalData(data.slice(0, toIndex));
          }, 400);
        }
      }
    }

    window.addEventListener('scroll', onScroll, true);

    return (): void => {
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [
    internalData,
    setInternalData,
    data,
    limit,
    loadData,
    loadWhenDistanceToBottom,
    setLoadingData
  ]);

  return (
    <>
      {internalData?.map((x: any, index: number) => {
        return renderItem({ item: x, index });
      })}
      <ListUtilityLoadingSpinner loading={loading} LoadingComponent={LoadingComponent} />
    </>
  );
};

export default LazyLoadingList;
